import { useInvariableEffect, useInitDependency } from '@servicetitan/cp-react-hooks';
import { DocumentTitle, PageLoader, portalStoreToken } from '@servicetitan/cp-ui';
import { CultureProvider } from '@servicetitan/culture';
import { DateTimeUtils } from '@servicetitan/datetime-utils';
import { LogService } from '@servicetitan/log-service';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { AuthApi, SettingsApi } from 'api';
import { CustomerPortalEnvApi } from 'api/src/env-api';
import { FeaturesApi } from 'api/src/features-api';
import { observer } from 'mobx-react';
import { FC, StrictMode, useEffect } from 'react';
import {
    BrowserRouter,
    Routes,
    Route,
    useLocation,
    useNavigate,
    Navigate,
    To,
} from 'react-router-dom';

import './design-system.css';
import './app.css';

import { AuthorizedApp } from './authorized.app';
import { AuthModule } from './modules/auth/components/auth.module';
import { GoogleIntermediateProvider } from './modules/auth/components/google';
import { PortalAuthStore } from './modules/auth/stores/portal-auth.store';
import { useAuthHistory } from './modules/auth/utils/use-auth-history';
import { AppError } from './modules/common/components/app-error/app-error';
import { AppNotifications } from './modules/common/components/notifications/notifications';
import { AppSettingsStore } from './modules/common/stores/app-settings.store';
import { EnvStore } from './modules/common/stores/env.store';
import { WebPortalStore } from './modules/common/stores/portal.store';

export const NotAuthorizedApp: FC = provide({
    singletons: [SettingsApi, AppSettingsStore, FeaturesApi],
})(
    observer(() => {
        const [
            { authorized, authorizedLoginless, authorizing, init, redirect },
            { loadingSettings },
        ] = useDependencies(PortalAuthStore, AppSettingsStore);

        const authHistory = useAuthHistory();
        const location = useLocation();

        useInvariableEffect(() => {
            const path = location.pathname + location.search;

            init(path && path !== '/' ? path : undefined);
        });

        return authorizing || loadingSettings ? (
            <PageLoader />
        ) : authorized || authorizedLoginless ? (
            <AuthorizedApp />
        ) : (
            <Navigate
                to={authHistory.path(
                    'login',
                    undefined,
                    redirect ? { redirect: encodeURI(redirect) } : undefined
                )}
            />
        );
    })
);

export const AppRouter = () => {
    const [{ onNavigate }] = useDependencies<[WebPortalStore]>(portalStoreToken as any);
    const navigate = useNavigate();

    useEffect(() => {
        onNavigate((to: To) => {
            navigate(to);
        });

        return () => {
            onNavigate();
        };
    }, [navigate, onNavigate]);

    return (
        <Routes>
            <Route path="/auth/*" element={<AuthModule />} />
            <Route path="*" element={<NotAuthorizedApp />} />
        </Routes>
    );
};

const AppUnwrapped = () => {
    return (
        <GoogleIntermediateProvider>
            <AppNotifications />
            <BrowserRouter>
                <AppRouter />
            </BrowserRouter>
        </GoogleIntermediateProvider>
    );
};

export const App: FC = provide({
    singletons: [
        AuthApi,
        DateTimeUtils,
        PortalAuthStore,
        { provide: portalStoreToken, useClass: WebPortalStore },
        CustomerPortalEnvApi,
        EnvStore,
    ],
})(() => {
    const { culture, settings } = useInitDependency<WebPortalStore>(portalStoreToken as any);

    return (
        <CultureProvider value={culture}>
            <StrictMode>
                <LogService endpoint="/api/log" storageKey="LogStorage_CustomerPortal">
                    <DocumentTitle title={settings?.title ?? ''} />
                    {settings ? <AppUnwrapped /> : <AppError />}
                </LogService>
            </StrictMode>
        </CultureProvider>
    );
});
